/**
 * Audit Contact Timeline Handler
 * Query handler for reading and analyzing a contact's activity history using GraphQL
 */

import type { ToolExecutionContext, ToolResult } from '../../types';
import type { Communications } from '@/src/gql/graphql';
import { QueryHandlerBase } from '../base';
import { uuidField, numberField, arrayField, booleanField } from '../../utils/schema-builder';
import { createUserGraphQLClient } from '../../utils/graphql-client';

/**
 * Timeline audit result
 */
export interface TimelineAuditResult {
  type: 'timeline_audit';
  contact_id: string;
  viewInApp?: string;
  activities: Partial<Communications>[];
  summary: {
    total_count: number;
    date_range: { from: string; to: string };
    activity_types: Record<string, number>;
    last_activity: Partial<Communications> | null;
    days_since_last_activity: number;
    inbound_count: number;
    outbound_count: number;
    pending_items: number;
  };
}

/**
 * Audit contact timeline handler
 * Uses GraphQL queries to fetch contact activity history with RLS enforcement
 */
export class AuditContactTimelineHandler extends QueryHandlerBase<TimelineAuditResult> {
  id = 'audit_contact_timeline';

  schema = {
    type: 'object',
    properties: {
      contact_id: uuidField('UUID of the contact to audit'),
      limit: numberField('Number of activities to return (default: 20, max: 100)', {
        minimum: 1,
        maximum: 100,
      }),
      days_back: numberField('How many days back to look (default: 30)', { minimum: 1 }),
      activity_types: arrayField('Optional filter by activity types (e.g., email, call, meeting)', {
        type: 'string',
      }),
      include_metadata: booleanField('Include full metadata in results (default: false)'),
    },
    required: ['contact_id'],
  };

  async execute(params: Record<string, any>, context?: ToolExecutionContext): Promise<ToolResult> {
    const limit = Math.min(params.limit || 20, 100);
    const daysBack = params.days_back || 30;
    const now = new Date();
    const startDate = new Date(now.getTime() - daysBack * 24 * 60 * 60 * 1000);

    if (!context?.userToken) {
      throw new Error('User token is required for timeline audit');
    }

    try {
      // Dynamically import GraphQL types and documents to avoid circular dependencies
      const { GetContactsDocument, GetCommunicationssDocument } = await import('@/src/gql/graphql');
      
      // Create user-scoped GraphQL client that respects RLS
      const client = createUserGraphQLClient(context.userToken);

      // Query contact with activities using GraphQL
      const result = await client.query({
        query: GetContactsDocument,
        variables: {
          id: params.contact_id,
        },
        fetchPolicy: 'network-only',
      });

      if (result.error) {
        throw new Error(`GraphQL error: ${result.error.message}`);
      }

      const data = result.data as any;
      const contact = data?.contactsCollection?.edges?.[0]?.node;
      if (!contact) {
        throw new Error(`Contact with ID "${params.contact_id}" not found`);
      }

      // Query contact activities using GraphQL
      const activitiesResult = await client.query({
        query: GetCommunicationssDocument,
        variables: {
          first: limit,
          filter: {
            contact_id: { eq: params.contact_id },
            created_at: {
              gte: startDate.toISOString(),
              lte: now.toISOString(),
            },
          },
          orderBy: [{ created_at: 'DescNullsLast' }],
        } as Parameters<typeof client.query>[0]['variables'],
        fetchPolicy: 'network-only',
      });

      if (activitiesResult.error) {
        throw new Error(`GraphQL error fetching activities: ${activitiesResult.error.message}`);
      }

      const activitiesData = activitiesResult.data as { communicationsCollection?: { edges?: Array<{ node?: unknown }> } } | undefined;
      const activities = activitiesData?.communicationsCollection?.edges
        ?.map((edge: { node?: unknown }) => edge?.node)
        .filter((node: unknown): node is NonNullable<unknown> => node != null) || [];
      const activityTypeCounts: Record<string, number> = {};
      let inboundCount = 0;
      let outboundCount = 0;
      let pendingItems = 0;
      let lastActivity: any = null;

      // If activities were returned from GraphQL, process them here
      // Filter by date range
      const filteredActivities = activities.filter((activity: any) => {
        const activityDate = new Date(activity.created_at);
        return activityDate >= startDate && activityDate <= now;
      });

      // Filter by activity types if specified
      let finalActivities = filteredActivities;
      if (params.activity_types && params.activity_types.length > 0) {
        finalActivities = filteredActivities.filter((activity: any) =>
          params.activity_types.includes(activity.type)
        );
      }

      // Sort by date descending and limit
      finalActivities = finalActivities
        .sort((a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
        .slice(0, limit);

      // Calculate summary statistics
      for (const activity of finalActivities) {
        const a = activity as { type?: string; direction?: string; status?: string; created_at?: string };
        const type = a.type || 'unknown';
        activityTypeCounts[type] = (activityTypeCounts[type] || 0) + 1;

        if (a.direction === 'inbound') inboundCount++;
        if (a.direction === 'outbound') outboundCount++;
        if (a.status === 'pending') pendingItems++;
      }

      if (finalActivities.length > 0) {
        lastActivity = finalActivities[0];
      }

      const daysSinceLastActivity = lastActivity
        ? Math.floor((now.getTime() - new Date(lastActivity.created_at).getTime()) / (24 * 60 * 60 * 1000))
        : -1;

      const viewInApp = `[View Contact](/contacts/${params.contact_id})`;
      const result_data: TimelineAuditResult = {
        type: 'timeline_audit',
        contact_id: params.contact_id,
        viewInApp,
        activities: params.include_metadata ? finalActivities : finalActivities.map((a: any) => ({
          id: a.id,
          type: a.type,
          created_at: a.created_at,
          description: a.description,
          direction: a.direction,
          status: a.status,
        })),
        summary: {
          total_count: finalActivities.length,
          date_range: {
            from: startDate.toISOString(),
            to: now.toISOString(),
          },
          activity_types: activityTypeCounts,
          last_activity: (lastActivity
          ? {
              id: lastActivity.id,
              type: lastActivity.type,
              created_at: lastActivity.created_at,
              description: lastActivity.description,
            }
          : null) as Partial<Communications> | null,
          days_since_last_activity: daysSinceLastActivity,
          inbound_count: inboundCount,
          outbound_count: outboundCount,
          pending_items: pendingItems,
        },
      };

      return {
        type: 'success',
        data: result_data,
        metadata: {
          toolId: this.id,
          contact: {
            id: contact.id,
            name: contact.name,
            email: contact.email,
          },
        },
      };
    } catch (error) {
      throw new Error(`Timeline audit failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }
}
